import React from "react"
import { useChargeDetection } from "@/chargeDetection/api"
import { WINDOW_MINUTES } from "@/chargeDetection/stepConfig"

export interface UseDetectionPipelineParams {
  assetId: number
  fakeDateTime: Date | null
}

export interface UseDetectionPipelineResult {
  api: ReturnType<typeof useChargeDetection>
  isRunning: boolean
  runDetection: () => Promise<void>
}

export function useDetectionPipeline({
  assetId,
  fakeDateTime,
}: UseDetectionPipelineParams): UseDetectionPipelineResult {
  const [isRunning, setIsRunning] = React.useState<boolean>(false)
  const api = useChargeDetection()

  const runDetection = React.useCallback(async () => {
    setIsRunning(true)

    // Reset all mutations including fallback
    api.step1DetectOnPeriods.reset()
    api.step1FallbackDetectOnPeriod.reset()
    api.step2ValidateCycle.reset()
    api.step3ExtractFeatures.reset()
    api.step4Predict.reset()
    api.submitFeedback.reset()

    try {
      const effectiveDate: Date = fakeDateTime || new Date()
      let step1Result

      // Step 1: Detect on-periods with fallback logic
      try {
        step1Result = await api.step1DetectOnPeriods.mutateAsync({
          installation: assetId,
          target_time: effectiveDate.toISOString(),
        })
      } catch (error: any) {
        step1Result = await api.step1FallbackDetectOnPeriod.mutateAsync({
          installation: assetId,
        })
      }

      // Step 2: Validate cycle
      const step2ValidateCycleResult = await api.step2ValidateCycle.mutateAsync({
        installation: assetId,
        on_start: step1Result.selected_start,
        on_end: step1Result.selected_end,
        window_minutes: WINDOW_MINUTES,
      })

      // Step 3: Extract features
      const step3ExtractFeaturesResult = await api.step3ExtractFeatures.mutateAsync({
        installation: assetId,
        on_start: step1Result.selected_start,
        on_end: step1Result.selected_end,
        window_minutes: WINDOW_MINUTES,
      })

      // Step 4: Predict
      await api.step4Predict.mutateAsync({
        features: step3ExtractFeaturesResult.features,
      })
    } catch (error) {
      // Errors are captured in mutation state
      console.error('Detection pipeline failed:', error)
    } finally {
      setIsRunning(false)
    }
  }, [assetId, fakeDateTime, api])

  return { api, isRunning, runDetection }
}
